import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionGroup,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  getDoc,
  query,
  where,
  deleteField,
} from '@angular/fire/firestore';
import { Observable, from, map, catchError, of, switchMap } from 'rxjs';
import { CollectionTables } from '../models/CollectionTables.model';
import {
  FirestoreTimestampField,
  firestoreTimestampNow,
  prepareFirestoreWrite,
} from '../utils/firestore-timestamp.util';

export interface Profile {
  id?: string;
  unitId: string;
  propertyId: string;
  type: 'individual' | 'organization'; // Profile type: individual or organization
  tenure: 'owner' | 'tenant' | 'applicant'; // Tenure: owner, tenant, or applicant
  name: string;
  email: string;
  phone: string;
  documents?: Document[];
  userId?: string; // Added when user accepts invitation
  businessId?: string;
  /** Property address object from property document. */
  propertyAddress?: Record<string, unknown>;
  /** Unit name/number (Resident mobile app). */
  unitNumber?: string;
  /** @deprecated legacy field — not written by admin app */
  unit?: string;
  /** @deprecated legacy field — not written by admin app */
  administratorId?: string;
  createdOn?: FirestoreTimestampField;
  updatedOn?: FirestoreTimestampField;
}

export interface Document {
  id?: string;
  fileName: string;
  filePath: string;
  downloadUrl: string;
  mimeType: string;
  totalBytes: number;
  createdOn: FirestoreTimestampField;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private firestore = inject(Firestore);

  /**
   * Get profiles for a unit
   * Path: properties/{propertyId}/profiles (same level as units); filter by unitId (and optionally tenure)
   */
  getProfilesByUnit(propertyId: string, unitId: string, tenure?: 'owner' | 'tenant' | 'applicant'): Observable<Profile[]> {
    const profilesPath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}`;
    const profilesRef = collection(this.firestore, profilesPath);
    
    let q = query(profilesRef, where('unitId', '==', unitId));
    if (tenure) {
      q = query(profilesRef, where('unitId', '==', unitId), where('tenure', '==', tenure));
    }

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (snapshot.empty) {
          return [];
        }

        const profiles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        } as Profile));

        return profiles;
      }),
      catchError((error) => {
        console.error('Error fetching profiles:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a single profile by ID
   * Path: properties/{propertyId}/profiles/{profileId}
   */
  getProfileById(propertyId: string, _unitId: string, profileId: string): Observable<Profile | null> {
    const profilePath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}/${profileId}`;
    const profileRef = doc(this.firestore, profilePath);

    return from(getDoc(profileRef)).pipe(
      map((snapshot) => {
        if (!snapshot.exists()) {
          return null;
        }

        return {
          id: snapshot.id,
          ...snapshot.data(),
        } as Profile;
      }),
      catchError((error) => {
        console.error('Error fetching profile:', error);
        return of(null);
      })
    );
  }

  /** Find a profile document by id when propertyId is unknown (legacy edit routes). */
  findProfileByDocumentId(profileId: string): Observable<Profile | null> {
    const profilesQuery = collectionGroup(this.firestore, CollectionTables.profiles);
    return from(getDocs(profilesQuery)).pipe(
      map((snapshot) => {
        const match = snapshot.docs.find((d) => d.id === profileId);
        if (!match) return null;
        return { id: match.id, ...match.data() } as Profile;
      }),
      catchError((error) => {
        console.error('Error finding profile by id:', error);
        return of(null);
      })
    );
  }

  /**
   * Add a new profile
   * Path: properties/{propertyId}/profiles (document includes unitId to associate with unit)
   * @param administratorId Optional – current administrator ID (set when adding/editing from admin app)
   */
  addProfile(propertyId: string, unitId: string, profileData: Partial<Profile>, administratorId?: string): Observable<string> {
    const profilesPath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}`;
    const profilesCollectionRef = collection(this.firestore, profilesPath);
    
    // Generate a unique document ID
    const profileRef = doc(profilesCollectionRef);
    const generatedId = profileRef.id;

    const cleanedProfile: Record<string, unknown> = {
      id: generatedId,
      unitId: unitId,
      propertyId: propertyId,
      type: profileData.type || 'individual', // Default to 'individual' if not provided
      tenure: profileData.tenure || 'applicant', // Default to 'applicant' if not provided
      name: profileData.name || '',
      email: profileData.email || '',
      phone: profileData.phone || '',
      createdOn: firestoreTimestampNow(),
      updatedOn: firestoreTimestampNow(),
    };

    // Add optional fields (bracket notation for index signature)
    if (administratorId) {
      cleanedProfile['businessId'] = administratorId;
    }
    if (profileData.documents && profileData.documents.length > 0) {
      cleanedProfile['documents'] = profileData.documents;
    }
    if (profileData.userId) {
      cleanedProfile['userId'] = profileData.userId;
    }
    if (profileData.propertyAddress) {
      cleanedProfile['propertyAddress'] = profileData.propertyAddress;
    }
    if (profileData.unitNumber !== undefined) {
      cleanedProfile['unitNumber'] = profileData.unitNumber;
    } else if (profileData.unit !== undefined) {
      cleanedProfile['unitNumber'] = profileData.unit;
    }

    return from(setDoc(profileRef, prepareFirestoreWrite(cleanedProfile) as Record<string, unknown>)).pipe(
      map(() => {
        console.log('✅ Profile added successfully:', generatedId);
        return generatedId;
      }),
      catchError((error) => {
        console.error('Error adding profile:', error);
        throw error;
      })
    );
  }

  /**
   * Update a profile
   * Path: properties/{propertyId}/profiles/{profileId}
   * @param administratorId Optional – current administrator ID (set when editing from admin app)
   */
  updateProfile(propertyId: string, _unitId: string, profileId: string, profileData: Partial<Profile>, administratorId?: string): Observable<void> {
    const profilePath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}/${profileId}`;
    const profileRef = doc(this.firestore, profilePath);

    const cleanedUpdateData: Record<string, unknown> = {
      updatedOn: firestoreTimestampNow(),
    };

    if (administratorId !== undefined) {
      cleanedUpdateData['businessId'] = administratorId;
    }
    if (profileData.type !== undefined) {
      cleanedUpdateData['type'] = profileData.type;
    }
    if (profileData.tenure !== undefined) {
      cleanedUpdateData['tenure'] = profileData.tenure;
    }
    if (profileData.name !== undefined) {
      cleanedUpdateData['name'] = profileData.name;
    }
    if (profileData.email !== undefined) {
      cleanedUpdateData['email'] = profileData.email;
    }
    if (profileData.phone !== undefined) {
      cleanedUpdateData['phone'] = profileData.phone;
    }
    if (profileData.documents !== undefined) {
      cleanedUpdateData['documents'] = profileData.documents;
    }
    if (profileData.userId !== undefined) {
      cleanedUpdateData['userId'] = profileData.userId;
    }
    if (profileData.propertyAddress !== undefined) {
      cleanedUpdateData['propertyAddress'] = profileData.propertyAddress;
    }
    if (profileData.unitNumber !== undefined) {
      cleanedUpdateData['unitNumber'] = profileData.unitNumber;
    } else if (profileData.unit !== undefined) {
      cleanedUpdateData['unitNumber'] = profileData.unit;
    }
    if (profileData.administratorId !== undefined) {
      cleanedUpdateData['businessId'] = profileData.administratorId;
    }
    if (profileData.businessId !== undefined) {
      cleanedUpdateData['businessId'] = profileData.businessId;
    }

    return from(
      updateDoc(profileRef, {
        ...(prepareFirestoreWrite(cleanedUpdateData) as Record<string, unknown>),
        unit: deleteField(),
        administratorId: deleteField(),
      })
    ).pipe(
      map(() => {
        console.log('✅ Profile updated successfully:', profileId);
      }),
      catchError((error) => {
        console.error('Error updating profile:', error);
        throw error;
      })
    );
  }

  /**
   * Delete a profile
   * Path: properties/{propertyId}/profiles/{profileId}
   */
  deleteProfile(propertyId: string, _unitId: string, profileId: string): Observable<void> {
    const profilePath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}/${profileId}`;
    const profileRef = doc(this.firestore, profilePath);

    return from(deleteDoc(profileRef)).pipe(
      map(() => {
        console.log('✅ Profile deleted successfully:', profileId);
      }),
      catchError((error) => {
        console.error('Error deleting profile:', error);
        throw error;
      })
    );
  }

  /**
   * Get all applicants for a property
   * Path: properties/{propertyId}/profiles; filter by tenure == 'applicant'
   */
  getApplicantsByProperty(propertyId: string): Observable<Profile[]> {
    const profilesPath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}`;
    const profilesRef = collection(this.firestore, profilesPath);
    const q = query(profilesRef, where('tenure', '==', 'applicant'));

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (snapshot.empty) {
          return [];
        }

        const profiles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        } as Profile));

        return profiles;
      }),
      catchError((error) => {
        console.error('Error fetching applicants:', error);
        return of([]);
      })
    );
  }

  /**
   * Tenants + owners for a property (excludes applicants).
   * Path: properties/{propertyId}/profiles; tenure != 'applicant'
   */
  getNonApplicantsByProperty(propertyId: string): Observable<Profile[]> {
    const profilesPath = `${CollectionTables.properties}/${propertyId}/${CollectionTables.profiles}`;
    const profilesRef = collection(this.firestore, profilesPath);
    const q = query(profilesRef, where('tenure', '!=', 'applicant'));

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map(
          (d) =>
            ({
              id: d.id,
              ...d.data(),
            }) as Profile
        )
      ),
      catchError((error) => {
        console.error('Error fetching non-applicant profiles:', error);
        return of([]);
      })
    );
  }

  /**
   * Count profiles linked to a management business (tenants + owners, not applicants).
   */
  countNonApplicantProfilesForBusiness(businessId: string): Observable<number> {
    const profilesQuery = collectionGroup(this.firestore, CollectionTables.profiles);
    return from(getDocs(profilesQuery)).pipe(
      map((snapshot) =>
        snapshot.docs.filter((d) => {
          const data = d.data();
          const bid = data['businessId'] || data['administratorId'];
          const tenure = data['tenure'];
          return bid === businessId && tenure && tenure !== 'applicant';
        }).length
      ),
      catchError((error) => {
        console.error('Error counting profiles:', error);
        return of(0);
      })
    );
  }

  /**
   * Convert applicant to tenant (change type and delete other applicants for the same unit)
   */
  convertApplicantToTenant(propertyId: string, unitId: string, profileId: string): Observable<void> {
    // First, update the applicant to tenant
    return this.updateProfile(propertyId, unitId, profileId, { tenure: 'tenant' }).pipe(
      switchMap(() => {
        // Get all other applicants for this unit
        return this.getProfilesByUnit(propertyId, unitId, 'applicant').pipe(
          switchMap((applicants) => {
            // Delete all other applicants
            const deleteTasks = applicants
              .filter(app => app.id !== profileId)
              .map(app => this.deleteProfile(propertyId, unitId, app.id!));

            if (deleteTasks.length === 0) {
              return of(void 0);
            }

            return from(Promise.all(deleteTasks.map(task => task.toPromise()))).pipe(
              map(() => void 0)
            );
          })
        );
      })
    );
  }

  // Legacy method names for backward compatibility during migration
  // These will be removed after full migration
  getAffiliationsByUnit(propertyId: string, unitId: string, tenure?: 'owner' | 'tenant' | 'applicant'): Observable<Profile[]> {
    return this.getProfilesByUnit(propertyId, unitId, tenure);
  }

  getAffiliationById(propertyId: string, unitId: string, affiliationId: string): Observable<Profile | null> {
    return this.getProfileById(propertyId, unitId, affiliationId);
  }

  addAffiliation(propertyId: string, unitId: string, affiliationData: Partial<Profile>): Observable<string> {
    return this.addProfile(propertyId, unitId, affiliationData);
  }

  updateAffiliation(propertyId: string, unitId: string, affiliationId: string, affiliationData: Partial<Profile>): Observable<void> {
    return this.updateProfile(propertyId, unitId, affiliationId, affiliationData);
  }

  deleteAffiliation(propertyId: string, unitId: string, affiliationId: string): Observable<void> {
    return this.deleteProfile(propertyId, unitId, affiliationId);
  }
}
